import { useEffect, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { ChatView } from "./components/ChatView";
import { ConnectionBanner } from "./components/ConnectionBanner";
import { ConversationList } from "./components/ConversationList";
import { PairingScreen } from "./components/PairingScreen";
import { SetupScreen } from "./components/SetupScreen";
import {
  canPrompt,
  isInstalled,
  needsManualInstall,
  onInstallStateChange,
  promptInstall,
} from "./lib/install";
import type { EnterPreference, ThemePreference } from "./lib/preferences";
import { LANGUAGES, LANGUAGE_NAMES, type Language, type MessageKey } from "./lib/i18n";
import { useT } from "./lib/useT";
import { ModelPicker } from "./components/ModelPicker";
import { OriginScreen } from "./components/OriginScreen";
import { CopyBlock } from "./components/CopyBlock";
import { Sheet } from "./components/Sheet";
import {
  IconArchive,
  IconRename,
  IconRestore,
  IconBell,
  IconInstall,
  IconModel,
  IconSpeaker,
  IconTrash,
  IconPin,
} from "./components/icons";
import { conversationTitle, useStore } from "./state/store";

type Dialog =
  | { kind: "none" }
  | { kind: "compose" }
  | { kind: "menu"; key: string }
  | { kind: "rename"; key: string; value: string }
  | { kind: "confirm-delete"; key: string }
  | { kind: "model"; key: string }
  | { kind: "settings" };

function ComposeDialog({ onClose }: { onClose: () => void }) {
  const startConversation = useStore((state) => state.startConversation);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const t = useT();

  async function submit() {
    setBusy(true);
    await startConversation({ title, message });
    setBusy(false);
    onClose();
  }

  return (
    <Sheet
      title={t("compose.title")}
      description={t("compose.description")}
      onClose={onClose}
    >
      <div className="field">
        <label htmlFor="new-title">{t("compose.titleLabel")}</label>
        <input
          id="new-title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={t("compose.titlePlaceholder")}
          maxLength={120}
        />
        <p className="field-hint">{t("compose.titleHint")}</p>
      </div>
      <div className="field">
        <label htmlFor="new-message">{t("compose.messageLabel")}</label>
        <input
          id="new-message"
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder={t("compose.messagePlaceholder")}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !busy) {
              void submit();
            }
          }}
        />
      </div>
      <div className="sheet-actions">
        <button type="button" className="button-ghost" onClick={onClose} disabled={busy}>
          {t("action.cancel")}
        </button>
        <button type="button" className="button-primary" style={{ width: "auto" }} onClick={() => void submit()} disabled={busy}>
          {busy ? t("compose.submitting") : t("compose.submit")}
        </button>
      </div>
    </Sheet>
  );
}

function RenameDialog({
  initial,
  onCancel,
  onSubmit,
}: {
  initial: string;
  onCancel: () => void;
  onSubmit: (value: string) => void;
}) {
  const [value, setValue] = useState(initial);
  const t = useT();
  return (
    <Sheet title={t("rename.title")} onClose={onCancel}>
      <div className="field">
        <label htmlFor="rename-input">{t("rename.label")}</label>
        <input
          id="rename-input"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              onSubmit(value);
            }
          }}
          maxLength={120}
        />
        <p className="field-hint">{t("rename.hint")}</p>
      </div>
      <div className="sheet-actions">
        <button type="button" className="button-ghost" onClick={onCancel}>
          {t("action.cancel")}
        </button>
        <button
          type="button"
          className="button-primary"
          style={{ width: "auto" }}
          onClick={() => onSubmit(value)}
        >
          {t("action.save")}
        </button>
      </div>
    </Sheet>
  );
}

const THEME_OPTIONS: Array<[ThemePreference, MessageKey]> = [
  ["system", "settings.themeSystem"],
  ["light", "settings.themeLight"],
  ["dark", "settings.themeDark"],
];

const ENTER_OPTIONS: Array<[EnterPreference, MessageKey]> = [
  ["auto", "settings.enterAuto"],
  ["send", "settings.enterSend"],
  ["newline", "settings.enterNewline"],
];

/**
 * The language names itself, never translated: someone who landed in a language
 * they cannot read has to be able to find their way out of it.
 */
const LANGUAGE_OPTIONS: Array<[Language, string]> = LANGUAGES.map((code) => [
  code,
  LANGUAGE_NAMES[code],
]);

/** Plain-language gloss for the rights this device was granted. */
const SCOPE_KEYS: Record<string, MessageKey> = {
  "operator.read": "scope.operator.read",
  "operator.write": "scope.operator.write",
  "operator.admin": "scope.operator.admin",
  "operator.pairing": "scope.operator.pairing",
  "operator.talk": "scope.operator.talk",
};

/** A row of pills that behaves like a radio group. */
function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<[T, string]>;
  onChange: (value: T) => void;
}) {
  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {options.map(([option, text]) => (
        <button
          key={option}
          type="button"
          role="radio"
          aria-checked={value === option}
          className="segment"
          onClick={() => onChange(option)}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

function InstallSection() {
  const t = useT();
  const [, force] = useState(0);
  useEffect(() => onInstallStateChange(() => force((n) => n + 1)), []);

  if (isInstalled()) {
    return (
      <p className="field-hint" style={{ marginTop: 0 }}>
        {t("settings.installed")}
      </p>
    );
  }

  if (canPrompt()) {
    return (
      <div className="sheet-menu">
        <button type="button" onClick={() => void promptInstall()}>
          <IconInstall />
          {t("settings.installAction")}
        </button>
      </div>
    );
  }

  if (needsManualInstall()) {
    return (
      <p
        className="field-hint"
        style={{ marginTop: 0 }}
        dangerouslySetInnerHTML={{ __html: t("settings.installSafari") }}
      />
    );
  }

  return (
    <p className="field-hint" style={{ marginTop: 0 }}>
      {t("settings.installManual")}
    </p>
  );
}

/**
 * What to do about a Gateway that has no VAPID key.
 *
 * "This Gateway has no VAPID key configured" is true and useless: it names a
 * thing most people have never heard of and gives them nowhere to go. So this
 * says what the key is *for*, and hands over the one route that works from the
 * device you are holding — the agent can edit its own configuration, and it is
 * reachable from a phone where a terminal is not.
 *
 * The prompt names `push.web.vapidPublicKey` on purpose: it is the call that
 * just came back empty, so it doubles as the agent's own way to check its work.
 * No config path is spelled out here, because this app cannot see the Gateway's
 * config schema and a guessed key would send people somewhere wrong.
 */
function PushSetupHelp() {
  const t = useT();
  return (
    <div className="setup-help">
      <p className="setup-help-title">{t("push.setupTitle")}</p>
      <p className="field-hint">{t("push.setupWhat")}</p>
      <p className="field-hint">{t("push.setupAsk")}</p>
      <CopyBlock value={t("push.setupPrompt")} />
      <p className="field-hint">{t("push.setupAfter")}</p>
    </div>
  );
}

function SettingsDialog({ onClose }: { onClose: () => void }) {
  const settings = useStore((state) => state.settings);
  const connectionState = useStore((state) => state.connectionState);
  const disconnect = useStore((state) => state.disconnect);
  const push = useStore((state) => state.push);
  const preferences = useStore((state) => state.preferences);
  const setPreference = useStore((state) => state.setPreference);
  const scopes = connectionState.scopes ?? [];
  const refreshPushState = useStore((state) => state.refreshPushState);
  const enablePush = useStore((state) => state.enablePush);
  const disablePush = useStore((state) => state.disablePush);
  const testPush = useStore((state) => state.testPush);
  const t = useT();

  useEffect(() => {
    void refreshPushState();
  }, [refreshPushState]);

  return (
    <Sheet title={t("settings.title")} onClose={onClose}>
      <div className="field">
        <label>{t("settings.language")}</label>
        <Segmented
          label={t("settings.language")}
          value={preferences.language}
          options={LANGUAGE_OPTIONS}
          onChange={(value) => setPreference("language", value)}
        />
      </div>

      <div className="field">
        <label>{t("settings.theme")}</label>
        <Segmented
          label={t("settings.theme")}
          value={preferences.theme}
          options={THEME_OPTIONS.map(([key, label]) => [key, t(label)])}
          onChange={(value) => setPreference("theme", value)}
        />
      </div>

      <div className="field">
        <label>{t("settings.enter")}</label>
        <Segmented
          label={t("settings.enter")}
          value={preferences.enter}
          options={ENTER_OPTIONS.map(([key, label]) => [key, t(label)])}
          onChange={(value) => setPreference("enter", value)}
        />
        <p className="field-hint">
          {preferences.enter === "auto"
            ? t("settings.enterAutoHint")
            : preferences.enter === "send"
              ? t("settings.enterSendHint")
              : t("settings.enterNewlineHint")}
        </p>
      </div>

      <div className="field">
        <label>{t("settings.install")}</label>
        <InstallSection />
      </div>

      <div className="field">
        <label>{t("settings.notifications")}</label>
        {push.supported ? (
          <>
            <div className="sheet-menu">
              <button
                type="button"
                onClick={() => void (push.enabled ? disablePush() : enablePush())}
                disabled={push.busy}
              >
                <IconBell />
                {push.busy
                  ? t("settings.pushBusy")
                  : push.enabled
                    ? t("settings.pushDisable")
                    : t("settings.pushEnable")}
              </button>
              {push.enabled ? (
                <button type="button" onClick={() => void testPush()}>
                  <IconBell />
                  {t("settings.pushTest")}
                </button>
              ) : null}
            </div>
            {push.setupNeeded ? <PushSetupHelp /> : null}
            <p className="field-hint">{t("settings.pushHint")}</p>
          </>
        ) : (
          <p className="field-hint" style={{ marginTop: 0 }}>
            {push.reason ?? t("settings.pushUnsupported")}
          </p>
        )}
      </div>
      <div className="field">
        <label>{t("settings.gateway")}</label>
        <p className="field-hint" style={{ marginTop: 0, wordBreak: "break-all" }}>
          {settings?.url ?? "—"}
        </p>
      </div>
      <div className="field">
        <label>{t("settings.status")}</label>
        <p className="field-hint" style={{ marginTop: 0 }}>
          {connectionState.phase === "connected"
            ? `${t("settings.statusConnected")}${connectionState.serverVersion ? ` · Gateway ${connectionState.serverVersion}` : ""}`
            : connectionState.phase}
        </p>
      </div>
      {scopes.length ? (
        <div className="field">
          <label>{t("settings.scopes")}</label>
          <ul className="scope-list">
            {scopes.map((scope) => (
              <li key={scope}>
                <code>{scope}</code>
                {SCOPE_KEYS[scope] ? <span>{t(SCOPE_KEYS[scope])}</span> : null}
              </li>
            ))}
          </ul>
          <p className="field-hint">{t("settings.scopesHint")}</p>
        </div>
      ) : null}

      {connectionState.deviceId ? (
        <div className="field">
          <label>{t("settings.deviceId")}</label>
          <p className="field-hint" style={{ marginTop: 0, wordBreak: "break-all" }}>
            {connectionState.deviceId.slice(0, 16)}…
          </p>
        </div>
      ) : null}
      <div className="sheet-menu">
        <button
          type="button"
          className="button-danger"
          onClick={() => {
            disconnect({ forgetDevice: true });
            onClose();
          }}
        >
          <IconTrash />
          {t("settings.disconnect")}
        </button>
      </div>
    </Sheet>
  );
}

function UpdatePrompt() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({ immediate: true });
  const t = useT();

  if (!needRefresh) {
    return null;
  }
  return (
    <div className="banner is-info">
      <span className="banner-text">{t("update.available")}</span>
      <button
        type="button"
        className="banner-action"
        onClick={() => {
          setNeedRefresh(false);
          void updateServiceWorker(true);
        }}
      >
        {t("update.reload")}
      </button>
    </div>
  );
}

export function App() {
  const settings = useStore((state) => state.settings);
  const restore = useStore((state) => state.restore);
  const activeKey = useStore((state) => state.activeKey);
  const conversations = useStore((state) => state.conversations);
  const connectionState = useStore((state) => state.connectionState);
  const scope = useStore((state) => state.scope);
  const openConversation = useStore((state) => state.openConversation);
  const rename = useStore((state) => state.rename);
  const archive = useStore((state) => state.archive);
  const togglePinned = useStore((state) => state.togglePinned);
  const remove = useStore((state) => state.remove);
  const ttsAvailable = useStore((state) => state.ttsAvailable);
  const speakLastAnswer = useStore((state) => state.speakLastAnswer);
  const connection = useStore((state) => state.connection);
  const t = useT();
  const [dialog, setDialog] = useState<Dialog>({ kind: "none" });

  useEffect(() => {
    restore();
  }, [restore]);

  // A PWA resumed from the background should reconnect at once rather than
  // waiting out whatever backoff it was in when the OS froze it.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        connection?.nudge();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onVisible);
    };
  }, [connection]);

  // A tapped notification names its conversation: through the URL when the app
  // had to be started, and through a worker message when a window was already
  // open. Both land in the same place.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wanted = params.get("chat");
    if (wanted) {
      void openConversation(wanted);
      window.history.replaceState({}, "", window.location.pathname);
    }

    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; sessionKey?: string | null } | undefined;
      if (data?.type === "open-chat" && data.sessionKey) {
        void openConversation(data.sessionKey);
      }
    };
    navigator.serviceWorker?.addEventListener("message", onMessage);
    return () => navigator.serviceWorker?.removeEventListener("message", onMessage);
  }, [openConversation]);

  // The phone's back gesture should leave the chat, not the app.
  useEffect(() => {
    if (!activeKey) {
      return;
    }
    window.history.pushState({ chat: activeKey }, "");
    const onPopState = () => void openConversation(null);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [activeKey, openConversation]);

  if (!settings) {
    return <SetupScreen />;
  }

  // Waiting for approval is a blocking state with a concrete action attached, so
  // it takes over the screen. Nothing behind it works anyway — the connection is
  // not established — and that includes a scope upgrade after the user asks for
  // broader rights, which needs its own approval and its own request id.
  if (connectionState.phase === "pairing") {
    return <PairingScreen />;
  }

  // Same shape of problem: the app cannot proceed, and the fix is one command
  // on the Gateway host. Retrying by itself would only repeat the refusal.
  if (connectionState.originBlocked) {
    return <OriginScreen />;
  }

  const menuRow =
    dialog.kind === "menu" ? conversations.find((row) => row.key === dialog.key) : undefined;
  const modelRow =
    dialog.kind === "model" ? conversations.find((row) => row.key === dialog.key) : undefined;

  return (
    <>
      <UpdatePrompt />
      <ConnectionBanner />
      <div className="app" data-view={activeKey ? "chat" : "list"}>
        <ConversationList
          onCompose={() => setDialog({ kind: "compose" })}
          onSettings={() => setDialog({ kind: "settings" })}
          onRowMenu={(key) => setDialog({ kind: "menu", key })}
        />
        <ChatView
          onBack={() => void openConversation(null)}
          onMenu={() => activeKey && setDialog({ kind: "menu", key: activeKey })}
          onModel={() => activeKey && setDialog({ kind: "model", key: activeKey })}
        />
      </div>

      {dialog.kind === "compose" ? (
        <ComposeDialog onClose={() => setDialog({ kind: "none" })} />
      ) : null}

      {dialog.kind === "settings" ? (
        <SettingsDialog onClose={() => setDialog({ kind: "none" })} />
      ) : null}

      {dialog.kind === "model" && modelRow ? (
        <ModelPicker row={modelRow} onClose={() => setDialog({ kind: "none" })} />
      ) : null}

      {dialog.kind === "menu" && menuRow ? (
        <Sheet title={conversationTitle(menuRow)} onClose={() => setDialog({ kind: "none" })}>
          <div className="sheet-menu">
            <button
              type="button"
              onClick={() =>
                setDialog({
                  kind: "rename",
                  key: menuRow.key,
                  value: menuRow.label ?? conversationTitle(menuRow),
                })
              }
            >
              <IconRename />
              {t("menu.rename")}
            </button>
            <button
              type="button"
              onClick={() => {
                void togglePinned(menuRow.key, !menuRow.pinned);
                setDialog({ kind: "none" });
              }}
            >
              <IconPin size={19} />
              {menuRow.pinned ? t("menu.unpin") : t("menu.pin")}
            </button>
            <button
              type="button"
              onClick={() => {
                void archive(menuRow.key, scope !== "archived");
                setDialog({ kind: "none" });
              }}
            >
              {scope === "archived" ? <IconRestore /> : <IconArchive />}
              {scope === "archived" ? t("menu.restore") : t("menu.archive")}
            </button>
            <button
              type="button"
              onClick={() => setDialog({ kind: "model", key: menuRow.key })}
            >
              <IconModel />
              {t("menu.model")}
            </button>
            {ttsAvailable && menuRow.key === activeKey ? (
              <button
                type="button"
                onClick={() => {
                  void speakLastAnswer(menuRow.key);
                  setDialog({ kind: "none" });
                }}
              >
                <IconSpeaker />
                {t("menu.speak")}
              </button>
            ) : null}
            <button
              type="button"
              className="button-danger"
              onClick={() => setDialog({ kind: "confirm-delete", key: menuRow.key })}
            >
              <IconTrash />
              {t("menu.delete")}
            </button>
          </div>
        </Sheet>
      ) : null}

      {dialog.kind === "rename" ? (
        <RenameDialog
          initial={dialog.value}
          onCancel={() => setDialog({ kind: "none" })}
          onSubmit={(value) => {
            void rename(dialog.key, value);
            setDialog({ kind: "none" });
          }}
        />
      ) : null}

      {dialog.kind === "confirm-delete" ? (
        <Sheet
          title={t("delete.title")}
          description={t("delete.description")}
          onClose={() => setDialog({ kind: "none" })}
        >
          <div className="sheet-actions">
            <button
              type="button"
              className="button-ghost"
              onClick={() => setDialog({ kind: "none" })}
            >
              {t("action.cancel")}
            </button>
            <button
              type="button"
              className="button-ghost button-danger"
              onClick={() => {
                void remove(dialog.key);
                setDialog({ kind: "none" });
              }}
            >
              {t("delete.confirm")}
            </button>
          </div>
        </Sheet>
      ) : null}
    </>
  );
}
